import { useState, useEffect, useMemo, useRef } from 'react';
import {
  Plus,
  X,
  Clock,
  ChevronRight,
  ChevronDown,
  User,
  MapPin,
  UserPlus,
  ClipboardList,
  Building2,
  Users,
  ChevronsUpDown,
  Timer,
  CalendarX,
  Globe,
  Hash,
  Calendar,
  CalendarOff,
  Loader2,
  Trash2,
  FlaskConical,
  Brain,
  Receipt,
} from 'lucide-react';
import { toast } from 'sonner';
import { cn } from '@/lib/utils';
import { SELECTED_OUTLINE_BUTTON } from '@/components/custom/highlight';
import { AppointmentTypeSimulatorDialog } from '@/components/custom/AppointmentTypeSimulatorDialog';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Separator } from '@/components/ui/separator';
import { Switch } from '@/components/ui/switch';
import { Textarea } from '@/components/ui/textarea';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '@/components/ui/tabs';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { Collapsible, CollapsibleTrigger, CollapsibleContent } from '@/components/ui/collapsible';
import ScheduleBlocksPanel from './ScheduleBlocksPanel';
import { CodeAutocomplete } from '@/components/custom/code-autocomplete';
import { PreferredDaysGridEditor } from '@/components/custom/preferred-days-grid';
import { UnsavedChangesDialog } from '@/components/custom/unsaved-changes-dialog';
import {
  useAppointmentTypes,
  useCreateAppointmentType,
  useUpdateAppointmentType,
  useDeleteAppointmentType,
  useAppointmentTypeOverrides,
  useOverridesByProvider,
  useUpsertOverride,
} from '@/hooks/use-appointment-types';
import { useProviders, useProviderLocations } from '@/hooks/use-providers';
import {
  useAppointmentTypePreferences,
  useDeletePreference,
} from '@/hooks/use-appointment-type-matching';
import { CANCELLATION_FEE_NOTICE_HOURS, leadTimeUnitLabel } from '@vagusx/clients';
import type {
  AppointmentTypeResponse,
  AppointmentTypeOverrideResponse,
  ConstraintType,
  ConstraintConfig,
  Constraint,
} from '@vagusx/clients';

// ── Constants ───────────────────────────────────────────────────────────────────

const DAYS_OF_WEEK = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'] as const;

const COLOR_SWATCHES = [
  // Blues
  'var(--chart-1)',
  'var(--chart-2)',
  'var(--chart-3)',
  'var(--chart-4)',
  'var(--chart-5)',
  // Red / Orange / Yellow
  'oklch(0.637 0.237 25.331)',
  'oklch(0.720 0.220 55.000)',
  'oklch(0.820 0.170 90.000)',
  // Greens
  'oklch(0.723 0.219 149.579)',
  'oklch(0.627 0.194 149.214)',
  'oklch(0.527 0.154 150.069)',
  // Purples / Pink
  'oklch(0.585 0.233 277.117)',
  'oklch(0.496 0.265 301.924)',
  'oklch(0.680 0.200 340.000)',
  // Teal / Cyan
  'oklch(0.680 0.162 196.813)',
  'oklch(0.540 0.150 216.922)',
  // Neutrals
  'oklch(0.554 0.046 257.417)',
  'oklch(0.446 0.030 256.802)',
];

const CATEGORIES = ['Office Visit', 'Preventive', 'Virtual', 'Procedure', 'Behavioral Health'];

/** Hide number input spinner arrows */
const NO_SPINNERS =
  '[appearance:textfield] [&::-webkit-inner-spin-button]:appearance-none [&::-webkit-outer-spin-button]:appearance-none';

// ── Types ───────────────────────────────────────────────────────────────────────

/** Parsed override constraints map */
type OverrideConstraintMap = Record<string, { label: string; config: ConstraintConfig }>;

// ── Constraint Options ──────────────────────────────────────────────────────────

interface ConstraintOption {
  type: ConstraintType;
  label: string;
  description: string;
  icon: React.ComponentType<{ className?: string }>;
  /**
   * Where it can be added: `all` = the type and a provider override,
   * `provider` = a provider override only, `type` = the type only.
   */
  scope: 'all' | 'provider' | 'type';
  makeDefault: () => ConstraintConfig;
}

const CONSTRAINT_OPTIONS: ConstraintOption[] = [
  {
    type: 'requiresReferral',
    label: 'Requires Referral',
    description: 'Only bookable when a referring provider is on file.',
    icon: ClipboardList,
    scope: 'all',
    makeDefault: () => ({ type: 'requiresReferral', enabled: true }),
  },
  {
    type: 'leadTime',
    label: 'Lead Time',
    description: "Don't offer slots sooner than this far in advance.",
    icon: Clock,
    scope: 'all',
    // Default to business days — clinics almost always mean working days when
    // they set an advance-notice window (weekends don't count toward prep time).
    makeDefault: () => ({ type: 'leadTime', unit: 'days', limit: 1, dayMode: 'business' }),
  },
  {
    type: 'preferredDays',
    label: 'Preferred Days',
    description: 'Only offer this type on the selected days and times.',
    icon: Calendar,
    scope: 'all',
    // Start empty — the user paints the days/times they want on the grid.
    makeDefault: () => ({ type: 'preferredDays', days: [] }),
  },
  {
    type: 'maxPerDay',
    label: 'Booking Limits',
    description: 'Cap how many of this type are booked per day and/or week.',
    icon: Hash,
    scope: 'all',
    // Both limits blank by default — the user fills in the caps they want.
    makeDefault: () => ({ type: 'maxPerDay' }),
  },
  {
    type: 'maxNewPerDay',
    label: 'Max New Patients/Day',
    description: 'Cap how many new-patient slots are offered per day.',
    icon: UserPlus,
    scope: 'all',
    makeDefault: () => ({ type: 'maxNewPerDay', limit: 5 }),
  },
  {
    type: 'buffer',
    label: 'Buffer Between Appts',
    description: 'Keep gap time before and after each appointment.',
    icon: Timer,
    scope: 'all',
    makeDefault: () => ({ type: 'buffer', beforeMins: 0, afterMins: 15 }),
  },
  {
    type: 'recurringBlock',
    label: 'Recurring Block',
    description: 'Block recurring times (e.g. lunch) from booking.',
    icon: CalendarX,
    scope: 'all',
    makeDefault: () => ({ type: 'recurringBlock', blocks: [] }),
  },
  {
    type: 'businessHours',
    label: 'Business Hours',
    description: 'Set the bookable hours for each day of the week.',
    icon: Building2,
    scope: 'provider',
    makeDefault: (): ConstraintConfig => ({
      type: 'businessHours',
      schedule: DAYS_OF_WEEK.map((day) => ({
        day,
        enabled: !['Sat', 'Sun'].includes(day),
        startTime: '09:00',
        endTime: '17:00',
      })),
    }),
  },
  {
    type: 'allowWebScheduling',
    label: 'Web Scheduling',
    description: 'Let patients self-book this type online.',
    icon: Globe,
    scope: 'all',
    makeDefault: () => ({ type: 'allowWebScheduling', enabled: true, webName: '' }),
  },
  {
    type: 'patients',
    label: 'Patient Type',
    description: 'Only offer this type to new or existing patients.',
    icon: Users,
    scope: 'all',
    makeDefault: () => ({ type: 'patients', patientType: 'new' }),
  },
  {
    type: 'patientGender',
    label: 'Patient Gender',
    description: 'Only offer this type to a specific patient gender.',
    icon: User,
    scope: 'all',
    makeDefault: () => ({ type: 'patientGender', gender: 'All' }),
  },
  {
    type: 'cancellationFee',
    label: 'Cancellation Fee',
    description: `Fee the voice agent discloses when a patient cancels within ${CANCELLATION_FEE_NOTICE_HOURS} hours of the appointment.`,
    icon: Receipt,
    // Type-level only (ADR-290): the fee belongs to the appointment type, and
    // the voice agent reads it from the type, not from a provider override.
    scope: 'type',
    makeDefault: () => ({ type: 'cancellationFee', amountCents: 5000 }),
  },
];

/** "$50" for whole dollars, "$12.50" otherwise. */
function formatFeeCents(cents: number): string {
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    minimumFractionDigits: cents % 100 === 0 ? 0 : 2,
  }).format(cents / 100);
}

// ── Constraint Summary ──────────────────────────────────────────────────────────

function constraintSummary(config: ConstraintConfig): string {
  switch (config.type) {
    case 'requiresReferral':
      return config.enabled ? 'Yes' : 'No';
    case 'leadTime':
      return `${config.limit} ${leadTimeUnitLabel(config)}`;
    case 'preferredDays': {
      if (config.days.length === 0) return 'None';
      const hasCustomHours = Object.values(config.dayTimes ?? {}).some((w) => w.length > 0);
      return `${config.days.join(', ')}${hasCustomHours ? ' · custom hours' : ''}`;
    }
    case 'maxPerDay': {
      const parts: string[] = [];
      if (config.limit != null) parts.push(`${config.limit}/day`);
      if (config.weeklyLimit != null) parts.push(`${config.weeklyLimit}/week`);
      return parts.length === 0 ? 'No limit' : parts.join(' · ');
    }
    case 'maxNewPerDay':
      return String(config.limit);
    case 'buffer':
      return `${config.beforeMins} min before, ${config.afterMins} min after`;
    case 'recurringBlock':
      return config.blocks.length === 0
        ? 'No blocks'
        : `${config.blocks.length} block${config.blocks.length !== 1 ? 's' : ''}`;
    case 'businessHours': {
      const enabled = config.schedule.filter((d) => d.enabled);
      if (enabled.length === 0) return 'Closed';
      const allSame = enabled.every(
        (d) => d.startTime === enabled[0]!.startTime && d.endTime === enabled[0]!.endTime
      );
      if (allSame && enabled.length > 0) {
        return `${enabled[0]!.day}-${enabled[enabled.length - 1]!.day} ${enabled[0]!.startTime}-${enabled[0]!.endTime}`;
      }
      return `${enabled.length} days`;
    }
    case 'allowWebScheduling':
      return config.enabled ? (config.webName ? `Yes (${config.webName})` : 'Yes') : 'No';
    case 'patients':
      return config.patientType === 'new' ? 'New' : 'Existing';
    case 'patientGender':
      return config.gender;
    case 'cancellationFee':
      return `${formatFeeCents(config.amountCents)} within ${CANCELLATION_FEE_NOTICE_HOURS}h`;
  }
}

// ── Helpers ─────────────────────────────────────────────────────────────────────

/** Parse override constraints from API response (Record<string, unknown>) to typed map */
function parseOverrideConstraints(
  raw: Record<string, unknown> | undefined | null
): OverrideConstraintMap {
  if (!raw) return {};
  return raw as OverrideConstraintMap;
}

// ── Helper Components ───────────────────────────────────────────────────────────

function FieldError({ error }: { error?: string }): React.ReactElement | null {
  if (!error) return null;
  return <p className="text-xs text-destructive mt-1">{error}</p>;
}

function ColorPicker({
  value,
  onChange,
}: {
  value: string;
  onChange: (color: string) => void;
}): React.ReactElement {
  return (
    <div className="flex flex-wrap items-center gap-1.5">
      {COLOR_SWATCHES.map((color) => (
        <button
          key={color}
          type="button"
          onClick={() => onChange(color)}
          className={cn(
            'h-6 w-6 rounded-full border-2 transition-transform',
            value === color ? 'border-foreground scale-110' : 'border-transparent hover:scale-105'
          )}
          style={{ backgroundColor: color }}
        />
      ))}
    </div>
  );
}

// ── Constraint Editor ───────────────────────────────────────────────────────────

function ConstraintEditor({
  config,
  onChange,
}: {
  config: ConstraintConfig;
  onChange: (config: ConstraintConfig) => void;
}): React.ReactElement {
  switch (config.type) {
    case 'requiresReferral':
      return (
        <div className="flex items-center gap-2">
          <Switch
            checked={config.enabled}
            onCheckedChange={(enabled) => onChange({ ...config, enabled })}
          />
          <span className="text-sm">{config.enabled ? 'Yes' : 'No'}</span>
        </div>
      );

    case 'leadTime':
      return (
        <div className="flex items-center gap-2">
          <Input
            type="number"
            value={config.limit}
            onChange={(e) => onChange({ ...config, limit: parseInt(e.target.value) || 0 })}
            className={`w-20 h-8 text-sm ${NO_SPINNERS}`}
            min={0}
          />
          <Select
            value={config.unit}
            onValueChange={(value) => {
              const unit = value as 'days' | 'hours';
              // dayMode only applies to days — clear it on hours so a stale
              // 'business' flag (set then hidden) isn't persisted.
              onChange({ ...config, unit, dayMode: unit === 'days' ? config.dayMode : undefined });
            }}
          >
            <SelectTrigger className="w-24 h-8 text-sm">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="days">Days</SelectItem>
              <SelectItem value="hours">Hours</SelectItem>
            </SelectContent>
          </Select>
          {config.unit === 'days' && (
            <div className="flex items-center gap-2 pl-1">
              <Switch
                checked={config.dayMode === 'business'}
                onCheckedChange={(business) =>
                  onChange({ ...config, dayMode: business ? 'business' : 'calendar' })
                }
              />
              <span className="text-sm text-muted-foreground">
                {config.dayMode === 'business' ? 'Business days' : 'Calendar days'}
              </span>
            </div>
          )}
        </div>
      );

    case 'preferredDays':
      return (
        <PreferredDaysGridEditor
          value={config}
          onChange={(next) => onChange({ ...config, ...next })}
        />
      );

    case 'maxPerDay': {
      const parseLimit = (raw: string): number | undefined => {
        const trimmed = raw.trim();
        if (trimmed === '') return undefined;
        const n = parseInt(trimmed, 10);
        return Number.isNaN(n) ? undefined : Math.max(0, n);
      };
      return (
        <div className="flex items-end gap-3">
          <div className="space-y-1">
            <Label className="text-xs text-muted-foreground">Max per day</Label>
            <Input
              type="number"
              value={config.limit ?? ''}
              placeholder="No limit"
              onChange={(e) => onChange({ ...config, limit: parseLimit(e.target.value) })}
              className={`w-28 h-8 text-sm ${NO_SPINNERS}`}
              min={0}
            />
          </div>
          <div className="space-y-1">
            <Label className="text-xs text-muted-foreground">Max per week</Label>
            <Input
              type="number"
              value={config.weeklyLimit ?? ''}
              placeholder="No limit"
              onChange={(e) => onChange({ ...config, weeklyLimit: parseLimit(e.target.value) })}
              className={`w-28 h-8 text-sm ${NO_SPINNERS}`}
              min={0}
            />
          </div>
        </div>
      );
    }

    case 'maxNewPerDay':
      return (
        <Input
          type="number"
          value={config.limit}
          onChange={(e) => onChange({ ...config, limit: parseInt(e.target.value) || 0 })}
          className={`w-20 h-8 text-sm ${NO_SPINNERS}`}
          min={0}
        />
      );

    case 'buffer':
      return (
        <div className="flex items-center gap-3">
          <div className="space-y-1">
            <Label className="text-xs text-muted-foreground">Before (min)</Label>
            <Input
              type="number"
              value={config.beforeMins}
              onChange={(e) => onChange({ ...config, beforeMins: parseInt(e.target.value) || 0 })}
              className={`w-20 h-8 text-sm ${NO_SPINNERS}`}
              min={0}
            />
          </div>
          <div className="space-y-1">
            <Label className="text-xs text-muted-foreground">After (min)</Label>
            <Input
              type="number"
              value={config.afterMins}
              onChange={(e) => onChange({ ...config, afterMins: parseInt(e.target.value) || 0 })}
              className={`w-20 h-8 text-sm ${NO_SPINNERS}`}
              min={0}
            />
          </div>
        </div>
      );

    case 'recurringBlock':
      return (
        <div className="space-y-2">
          {config.blocks.map((block, idx) => (
            <div key={block.id} className="rounded border p-2 space-y-2">
              <div className="flex items-center justify-between">
                <span className="text-xs font-medium text-muted-foreground">Block {idx + 1}</span>
                <Button
                  type="button"
                  variant="ghost"
                  size="icon"
                  className="h-6 w-6 text-muted-foreground hover:text-destructive"
                  onClick={() =>
                    onChange({
                      ...config,
                      blocks: config.blocks.filter((b) => b.id !== block.id),
                    })
                  }
                >
                  <X className="h-3 w-3" />
                </Button>
              </div>
              <div className="flex gap-1">
                {DAYS_OF_WEEK.map((day) => {
                  const selected = block.days.includes(day);
                  return (
                    <Button
                      key={day}
                      type="button"
                      variant="outline"
                      size="sm"
                      className={cn('h-7 w-8 text-xs px-0', selected && SELECTED_OUTLINE_BUTTON)}
                      onClick={() => {
                        const days = selected
                          ? block.days.filter((d) => d !== day)
                          : [...block.days, day];
                        onChange({
                          ...config,
                          blocks: config.blocks.map((b) =>
                            b.id === block.id ? { ...b, days } : b
                          ),
                        });
                      }}
                    >
                      {day.slice(0, 2)}
                    </Button>
                  );
                })}
              </div>
              <div className="flex items-center gap-2">
                <Input
                  type="time"
                  value={block.startTime}
                  onChange={(e) =>
                    onChange({
                      ...config,
                      blocks: config.blocks.map((b) =>
                        b.id === block.id ? { ...b, startTime: e.target.value } : b
                      ),
                    })
                  }
                  className="h-7 text-xs w-28"
                />
                <span className="text-xs text-muted-foreground">to</span>
                <Input
                  type="time"
                  value={block.endTime}
                  onChange={(e) =>
                    onChange({
                      ...config,
                      blocks: config.blocks.map((b) =>
                        b.id === block.id ? { ...b, endTime: e.target.value } : b
                      ),
                    })
                  }
                  className="h-7 text-xs w-28"
                />
              </div>
              <Input
                value={block.reason}
                onChange={(e) =>
                  onChange({
                    ...config,
                    blocks: config.blocks.map((b) =>
                      b.id === block.id ? { ...b, reason: e.target.value } : b
                    ),
                  })
                }
                placeholder="Reason..."
                className="h-7 text-xs"
              />
            </div>
          ))}
          <Button
            type="button"
            variant="outline"
            size="sm"
            className="w-full h-7 text-xs"
            onClick={() =>
              onChange({
                ...config,
                blocks: [
                  ...config.blocks,
                  {
                    id: `rb-${Date.now()}`,
                    days: [],
                    startTime: '09:00',
                    endTime: '17:00',
                    reason: '',
                  },
                ],
              })
            }
          >
            <Plus className="h-3 w-3 mr-1" />
            Add Block
          </Button>
        </div>
      );

    case 'businessHours':
      return (
        <div className="space-y-1.5">
          {config.schedule.map((entry) => (
            <div key={entry.day} className="flex items-center gap-2">
              <span className="text-xs w-8 shrink-0">{entry.day}</span>
              <Switch
                checked={entry.enabled}
                onCheckedChange={(enabled) =>
                  onChange({
                    ...config,
                    schedule: config.schedule.map((s) =>
                      s.day === entry.day ? { ...s, enabled } : s
                    ),
                  })
                }
              />
              <Input
                type="time"
                value={entry.startTime}
                disabled={!entry.enabled}
                onChange={(e) =>
                  onChange({
                    ...config,
                    schedule: config.schedule.map((s) =>
                      s.day === entry.day ? { ...s, startTime: e.target.value } : s
                    ),
                  })
                }
                className="h-7 text-xs w-28"
              />
              <span className="text-xs text-muted-foreground">to</span>
              <Input
                type="time"
                value={entry.endTime}
                disabled={!entry.enabled}
                onChange={(e) =>
                  onChange({
                    ...config,
                    schedule: config.schedule.map((s) =>
                      s.day === entry.day ? { ...s, endTime: e.target.value } : s
                    ),
                  })
                }
                className="h-7 text-xs w-28"
              />
            </div>
          ))}
        </div>
      );

    case 'allowWebScheduling':
      return (
        <div className="space-y-2">
          <div className="flex items-center gap-2">
            <Switch
              checked={config.enabled}
              onCheckedChange={(enabled) => onChange({ ...config, enabled })}
            />
            <span className="text-sm">{config.enabled ? 'Enabled' : 'Disabled'}</span>
          </div>
          {config.enabled && (
            <div className="space-y-1">
              <Label className="text-xs text-muted-foreground">Web display name</Label>
              <Input
                value={config.webName}
                onChange={(e) => onChange({ ...config, webName: e.target.value })}
                placeholder="e.g. Walk-in Visit"
                className="h-8 text-sm"
              />
            </div>
          )}
        </div>
      );

    case 'patients':
      return (
        <Select
          value={config.patientType}
          onValueChange={(val) => onChange({ ...config, patientType: val as 'new' | 'existing' })}
        >
          <SelectTrigger className="w-40 h-8 text-sm">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="new">New Patients</SelectItem>
            <SelectItem value="existing">Existing Patients</SelectItem>
          </SelectContent>
        </Select>
      );

    case 'patientGender':
      return (
        <Select value={config.gender} onValueChange={(gender) => onChange({ ...config, gender })}>
          <SelectTrigger className="w-32 h-8 text-sm">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="Male">Male</SelectItem>
            <SelectItem value="Female">Female</SelectItem>
            <SelectItem value="All">All</SelectItem>
          </SelectContent>
        </Select>
      );

    case 'cancellationFee':
      return (
        <CancellationFeeInput
          amountCents={config.amountCents}
          onChange={(amountCents) => onChange({ ...config, amountCents })}
        />
      );
  }
}

/**
 * The fee in dollars, stored as whole cents. Keeps its own text so a partly
 * typed amount ("12.") is not reformatted under the cursor.
 */
function CancellationFeeInput({
  amountCents,
  onChange,
}: {
  amountCents: number;
  onChange: (amountCents: number) => void;
}): React.ReactElement {
  const [text, setText] = useState(() => (amountCents / 100).toString());
  const invalid = !(amountCents > 0);
  return (
    <div className="space-y-1">
      <div className="flex items-center gap-2">
        <span className="text-sm text-muted-foreground">$</span>
        <Input
          type="number"
          inputMode="decimal"
          value={text}
          onChange={(e) => {
            setText(e.target.value);
            const dollars = parseFloat(e.target.value);
            onChange(Number.isFinite(dollars) ? Math.round(dollars * 100) : 0);
          }}
          className={cn('w-24 h-8 text-sm', NO_SPINNERS, invalid && 'border-destructive')}
          min={0}
          step="0.01"
        />
        <span className="text-xs text-muted-foreground">
          if cancelled within {CANCELLATION_FEE_NOTICE_HOURS} hours
        </span>
      </div>
      <FieldError error={invalid ? 'Enter an amount greater than $0.' : undefined} />
    </div>
  );
}

// ── Override Components ─────────────────────────────────────────────────────────

function OverridableField({
  label,
  icon,
  defaultValue,
  overrideValue,
  onToggle,
  onChange,
}: {
  label: string;
  icon?: React.ReactNode;
  defaultValue: string;
  overrideValue: string | null;
  onToggle: (on: boolean) => void;
  onChange: (value: string) => void;
}): React.ReactElement {
  const isOverridden = overrideValue !== null;

  return (
    <div className="flex items-center gap-3">
      {icon && <div className="shrink-0">{icon}</div>}
      <Label className="text-sm shrink-0 min-w-[7rem]">{label}</Label>
      <div className="flex-1 min-w-0">
        {isOverridden ? (
          <div className="flex items-center gap-2">
            <Input
              value={overrideValue}
              onChange={(e) => onChange(e.target.value)}
              className="h-8 text-sm w-24"
            />
            <span className="text-xs text-muted-foreground line-through">{defaultValue}</span>
          </div>
        ) : (
          <span className="text-sm">{defaultValue}</span>
        )}
      </div>
      <Switch checked={isOverridden} onCheckedChange={onToggle} className="shrink-0" />
    </div>
  );
}

function ConstraintOverrideCard({
  constraint,
  defaultConfig,
  overrideConfig,
  onToggle,
  onChange,
}: {
  constraint: Constraint;
  defaultConfig: ConstraintConfig;
  overrideConfig: ConstraintConfig | undefined;
  onToggle: (on: boolean) => void;
  onChange: (config: ConstraintConfig) => void;
}): React.ReactElement {
  const isOverridden = overrideConfig !== undefined;
  const optDef = CONSTRAINT_OPTIONS.find((o) => o.type === constraint.type);
  const Icon = optDef?.icon ?? ClipboardList;

  return (
    <div className="flex items-start gap-3">
      <Icon className="h-4 w-4 mt-1 shrink-0 text-muted-foreground" />
      <div className="flex-1 min-w-0 space-y-2">
        <div className="flex items-center gap-2">
          <span className="text-sm font-medium shrink-0">{constraint.label}</span>
          {!isOverridden && (
            <span className="text-sm text-muted-foreground">
              {constraintSummary(defaultConfig)}
            </span>
          )}
        </div>
        {isOverridden && (
          <div className="space-y-1">
            <ConstraintEditor config={overrideConfig} onChange={onChange} />
            <span className="text-xs text-muted-foreground">
              was: {constraintSummary(defaultConfig)}
            </span>
          </div>
        )}
      </div>
      <Switch checked={isOverridden} onCheckedChange={onToggle} className="shrink-0 mt-0.5" />
    </div>
  );
}

// ── Preferences Panel ────────────────────────────────────────────────────────

function PreferencesPanel(): React.ReactElement {
  const { data: preferences = [], isLoading } = useAppointmentTypePreferences();
  const { data: types = [] } = useAppointmentTypes();
  const deletePref = useDeletePreference();

  const typeNameMap = useMemo(() => {
    const map = new Map<string, string>();
    for (const t of types) map.set(t.id, t.name);
    return map;
  }, [types]);

  function typeName(id: string): string {
    return typeNameMap.get(id) ?? id.slice(0, 8);
  }

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-40 text-muted-foreground">
        <Loader2 className="mr-2 h-5 w-5 animate-spin" />
        Loading preferences...
      </div>
    );
  }

  if (preferences.length === 0) {
    return (
      <div className="flex flex-col items-center justify-center h-40 text-center text-muted-foreground space-y-2 px-6">
        <Brain className="h-8 w-8 opacity-30" />
        <p className="text-sm">No scheduling notes yet</p>
        <p className="text-xs">
          When two appointment types tie in the simulator, pick the correct one and explain why. The
          system saves your decision and applies it automatically on future referrals.
        </p>
      </div>
    );
  }

  return (
    <ScrollArea className="flex-1">
      <div className="p-4 space-y-3">
        <p className="text-xs text-muted-foreground">
          Scheduling notes from past tie-breaks. The system uses these to automatically resolve
          future ties with similar referral signals.
        </p>
        {preferences.map((pref) => (
          <div key={pref.id} className="rounded-lg border p-3 space-y-2 text-sm">
            <div className="flex items-start justify-between gap-2">
              <div className="space-y-1 min-w-0">
                <p className="font-medium truncate">Chose: {typeName(pref.chosenTypeId)}</p>
                <p className="text-xs text-muted-foreground">
                  over{' '}
                  {pref.competingTypeIds
                    .filter((id) => id !== pref.chosenTypeId)
                    .map((id) => typeName(id))
                    .join(', ')}
                </p>
              </div>
              <Button
                variant="ghost"
                size="sm"
                className="h-7 w-7 p-0 shrink-0 text-muted-foreground hover:text-destructive"
                onClick={() => deletePref.mutate(pref.id)}
                disabled={deletePref.isPending}
              >
                <Trash2 className="h-3.5 w-3.5" />
              </Button>
            </div>

            <p className="text-xs italic text-muted-foreground">"{pref.reason}"</p>

            <div className="flex flex-wrap gap-1">
              {pref.situationCptCodes.map((code) => (
                <Badge key={code} variant="outline" className="text-[10px] py-0">
                  CPT {code}
                </Badge>
              ))}
              {pref.situationIcdCodes.map((code) => (
                <Badge key={code} variant="outline" className="text-[10px] py-0">
                  ICD {code}
                </Badge>
              ))}
              {pref.situationPatientType && (
                <Badge variant="outline" className="text-[10px] py-0">
                  {pref.situationPatientType} patient
                </Badge>
              )}
            </div>

            <div className="flex items-center gap-3 text-[10px] text-muted-foreground">
              <span>
                Applied {pref.applyCount} time{pref.applyCount !== 1 ? 's' : ''}
              </span>
              <span>
                Created{' '}
                {new Date(pref.decidedAt).toLocaleDateString(undefined, {
                  month: 'short',
                  day: 'numeric',
                  year: 'numeric',
                })}
              </span>
            </div>
          </div>
        ))}
      </div>
    </ScrollArea>
  );
}

// ── Component ───────────────────────────────────────────────────────────────────

export default function AppointmentSettingsPage(): React.ReactElement {
  // ── Server state ──────────────────────────────────────────────────────────────
  const { data: types = [], isLoading: typesLoading } = useAppointmentTypes();
  const { data: providersData, isLoading: providersLoading } = useProviders();
  const providers = providersData?.items ?? [];
  const createTypeMutation = useCreateAppointmentType();
  const updateTypeMutation = useUpdateAppointmentType();
  const deleteTypeMutation = useDeleteAppointmentType();
  const upsertOverrideMutation = useUpsertOverride();

  // ── UI state ──────────────────────────────────────────────────────────────────
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [activeTab, setActiveTab] = useState<string>('types');
  const [expandedProviders, setExpandedProviders] = useState<Set<string>>(new Set());
  const [selectedProviderId, setSelectedProviderId] = useState<string | null>(null);
  const [selectedLocationId, setSelectedLocationId] = useState<string | null>(null);
  const [expandedTypeCards, setExpandedTypeCards] = useState<Set<string>>(new Set());
  const [overridesOpen, setOverridesOpen] = useState(false);
  const [settingsSection, setSettingsSection] = useState<'types' | 'blocks' | 'preferences'>(
    'types'
  );
  const [showCreateForm, setShowCreateForm] = useState(false);
  const [simulatorOpen, setSimulatorOpen] = useState(false);
  // Id of a just-added constraint to scroll into view once it renders.
  const [newConstraintId, setNewConstraintId] = useState<string | null>(null);
  const [newTypeName, setNewTypeName] = useState('');
  const [newTypeShortName, setNewTypeShortName] = useState('');

  // Unsaved changes dialog
  const [showUnsavedDialog, setShowUnsavedDialog] = useState(false);
  const pendingActionRef = useRef<(() => void) | undefined>(undefined);

  /** Guard an action that would lose unsaved changes */
  const guardDirtyAction = (action: () => void): void => {
    if (isDirty) {
      pendingActionRef.current = action;
      setShowUnsavedDialog(true);
    } else {
      action();
    }
  };

  // Optimistic local editing state — prevents input revert on background refetch
  const [editingFields, setEditingFields] = useState<Record<string, unknown>>({});

  // Auto-select first type when data loads
  useEffect(() => {
    if (types.length > 0 && !selectedId) {
      setSelectedId(types[0]!.id);
    }
  }, [types, selectedId]);

  // Reset editing state when switching types
  useEffect(() => {
    setEditingFields({});
  }, [selectedId]);

  // ── Provider data ─────────────────────────────────────────────────────────────
  const { data: providerLocations = [] } = useProviderLocations(selectedProviderId ?? undefined);
  const { data: activeOverrides } = useOverridesByProvider(
    selectedProviderId ?? undefined,
    selectedLocationId ?? undefined
  );
  const { data: typeOverridesForSummary } = useAppointmentTypeOverrides(
    activeTab === 'types' ? (selectedId ?? undefined) : undefined
  );

  // ── Derived state ─────────────────────────────────────────────────────────────
  const serverType = types.find((t) => t.id === selectedId) ?? null;
  // Merge server data with local edits for display
  const selectedType = serverType
    ? ({ ...serverType, ...editingFields } as AppointmentTypeResponse)
    : null;
  const selectedProvider =
    activeTab === 'providers' && selectedProviderId
      ? (providers.find((p) => p.id === selectedProviderId) ?? null)
      : null;
  const selectedLocation =
    selectedProvider && selectedLocationId
      ? (providerLocations.find((pl) => pl.locationId === selectedLocationId) ?? null)
      : null;

  // Index overrides by typeId for O(1) lookups in provider view
  const overridesByTypeId = useMemo(() => {
    const map = new Map<string, AppointmentTypeOverrideResponse>();
    if (activeOverrides) {
      for (const o of activeOverrides) {
        map.set(o.appointmentTypeId, o);
      }
    }
    return map;
  }, [activeOverrides]);

  // ── Field-level validation errors ──────────────────────────────────────────────
  const [fieldErrors, setFieldErrors] = useState<Record<string, string>>({});
  const isDirty = Object.keys(editingFields).length > 0;

  // ── Type mutations ───────────────────────────────────────────────────────────

  const updateSelectedType = (updates: Partial<AppointmentTypeResponse>): void => {
    setEditingFields((prev) => ({ ...prev, ...updates }));
    // Clear errors for changed fields
    const keysToCheck = Object.keys(updates);
    const errorsToClear = keysToCheck.filter((k) => fieldErrors[k]);
    if (errorsToClear.length > 0) {
      setFieldErrors((prev) => {
        const next = { ...prev };
        for (const k of errorsToClear) delete next[k];
        return next;
      });
    }
  };

  /** Validate and save all pending changes */
  /** Validate and save. Returns true if validation passed (save initiated). */
  const saveType = (): boolean => {
    if (!selectedType || !selectedId) return false;

    // Validate required fields against the merged state (server + local edits)
    const errors: Record<string, string> = {};
    if (!selectedType.name?.trim()) errors.name = 'Name is required';
    if (!selectedType.shortName?.trim()) errors.shortName = 'Short name is required';
    if (!selectedType.durationMinutes || selectedType.durationMinutes < 5)
      errors.durationMinutes = 'Duration must be at least 5 minutes';
    if (!selectedType.color) errors.color = 'Color is required';

    // Check for duplicate name (against other types, not self)
    if (selectedType.name?.trim()) {
      const duplicate = types.find(
        (t) => t.id !== selectedId && t.name.toLowerCase() === selectedType.name.toLowerCase()
      );
      if (duplicate) errors.name = 'An appointment type with this name already exists';
    }

    if (Object.keys(errors).length > 0) {
      setFieldErrors(errors);
      return false;
    }

    setFieldErrors({});
    updateTypeMutation.mutate(
      { id: selectedId, data: editingFields as Record<string, unknown> },
      {
        onSuccess: () => {
          setEditingFields({});
          toast.success('Appointment type saved');
        },
      }
    );
    return true;
  };

  /** Discard local edits */
  const cancelEdits = (): void => {
    setEditingFields({});
    setFieldErrors({});
  };

  const addConstraint = (option: ConstraintOption): void => {
    if (!selectedType) return;
    const constraints = (selectedType.constraints ?? []) as Constraint[];
    if (constraints.some((c) => c.type === option.type)) return;
    const id = `c-${Date.now()}`;
    updateSelectedType({
      constraints: [
        ...constraints,
        { id, type: option.type, label: option.label, config: option.makeDefault() },
      ],
    } as Partial<AppointmentTypeResponse>);
    setNewConstraintId(id);
  };

  // Scroll a newly-added constraint card into view once it has rendered.
  useEffect(() => {
    if (!newConstraintId) return;
    const el = document.querySelector(`[data-constraint-id="${newConstraintId}"]`);
    el?.scrollIntoView({ behavior: 'smooth', block: 'start' });
    setNewConstraintId(null);
  }, [newConstraintId]);

  const removeConstraint = (constraintId: string): void => {
    if (!selectedType) return;
    const constraints = (selectedType.constraints ?? []) as Constraint[];
    updateSelectedType({
      constraints: constraints.filter((c) => c.id !== constraintId),
    } as Partial<AppointmentTypeResponse>);
  };

  const updateConstraintConfig = (constraintId: string, config: ConstraintConfig): void => {
    if (!selectedType) return;
    const constraints = (selectedType.constraints ?? []) as Constraint[];
    updateSelectedType({
      constraints: constraints.map((c) => (c.id === constraintId ? { ...c, config } : c)),
    } as Partial<AppointmentTypeResponse>);
  };

  const typeConstraints = (selectedType?.constraints ?? []) as Constraint[];
  const availableConstraints = CONSTRAINT_OPTIONS.filter(
    (opt) => opt.scope !== 'provider' && !typeConstraints.some((c) => c.type === opt.type)
  );

  // ── Provider override mutations ──────────────────────────────────────────────

  const upsertOverride = (
    appointmentTypeId: string,
    updates: Partial<{
      enabled: boolean;
      durationOverride: number | null;
      constraints: Record<string, unknown>;
    }>
  ): void => {
    if (!selectedProviderId) return;
    const existing = overridesByTypeId.get(appointmentTypeId);
    upsertOverrideMutation.mutate({
      appointmentTypeId,
      providerId: selectedProviderId,
      locationId: selectedLocationId ?? undefined,
      enabled: updates.enabled ?? existing?.enabled ?? true,
      durationOverride:
        updates.durationOverride !== undefined
          ? updates.durationOverride
          : (existing?.durationOverride ?? undefined),
      constraints:
        updates.constraints !== undefined
          ? updates.constraints
          : ((existing?.constraints as Record<string, unknown>) ?? undefined),
    });
  };

  const toggleProviderTypeEnabled = (typeId: string): void => {
    const existing = overridesByTypeId.get(typeId);
    const currentEnabled = existing?.enabled ?? true;
    upsertOverride(typeId, { enabled: !currentEnabled });
  };

  const setDurationOverride = (typeId: string, value: number): void => {
    upsertOverride(typeId, { durationOverride: value });
  };

  const clearDurationOverride = (typeId: string): void => {
    upsertOverride(typeId, { durationOverride: null });
  };

  const setConstraintOverride = (
    typeId: string,
    constraintType: string,
    label: string,
    config: ConstraintConfig
  ): void => {
    const existing = overridesByTypeId.get(typeId);
    const currentConstraints = parseOverrideConstraints(
      existing?.constraints as Record<string, unknown>
    );
    upsertOverride(typeId, {
      constraints: { ...currentConstraints, [constraintType]: { label, config } },
    });
  };

  const removeConstraintOverride = (typeId: string, constraintType: string): void => {
    const existing = overridesByTypeId.get(typeId);
    const currentConstraints = {
      ...parseOverrideConstraints(existing?.constraints as Record<string, unknown>),
    };
    delete currentConstraints[constraintType];
    upsertOverride(typeId, { constraints: currentConstraints });
  };

  const toggleProviderExpanded = (providerId: string): void => {
    setExpandedProviders((prev) => {
      const next = new Set(prev);
      if (next.has(providerId)) next.delete(providerId);
      else next.add(providerId);
      return next;
    });
  };

  const addConstraintFromOption = (typeId: string, option: ConstraintOption): void => {
    setConstraintOverride(typeId, option.type, option.label, option.makeDefault());
  };

  const resolveDefaultDuration = (typeId: string): number => {
    const typeDef = types.find((t) => t.id === typeId);
    if (!typeDef) return 0;
    if (selectedLocationId && selectedProviderId && activeOverrides) {
      const provOverride = activeOverrides.find(
        (o) => o.appointmentTypeId === typeId && !o.locationId
      );
      if (provOverride?.durationOverride != null) return provOverride.durationOverride;
    }
    return typeDef.durationMinutes;
  };

  const resolveDefaultConstraint = (
    typeId: string,
    constraintType: string
  ): ConstraintConfig | undefined => {
    const typeDef = types.find((t) => t.id === typeId);
    if (!typeDef) return undefined;
    if (selectedLocationId && selectedProviderId && activeOverrides) {
      const provOverride = activeOverrides.find(
        (o) => o.appointmentTypeId === typeId && !o.locationId
      );
      const provConstraints = parseOverrideConstraints(
        provOverride?.constraints as Record<string, unknown>
      );
      if (provConstraints[constraintType]) {
        return provConstraints[constraintType]!.config;
      }
    }
    const constraint = ((typeDef.constraints ?? []) as Constraint[]).find(
      (c) => c.type === constraintType
    );
    return constraint?.config;
  };

  // ── Render ────────────────────────────────────────────────────────────────────

  return (
    <div className="flex flex-col h-full overflow-hidden">
      {/* Top Tabs */}
      <div className="border-b px-4 pt-3 shrink-0">
        <div className="flex gap-4">
          <button
            onClick={() => setSettingsSection('types')}
            className={cn(
              'flex items-center gap-1.5 pb-2 text-sm font-medium border-b-2 transition-colors',
              settingsSection === 'types'
                ? 'border-primary text-foreground'
                : 'border-transparent text-muted-foreground hover:text-foreground'
            )}
          >
            <Clock className="h-4 w-4" />
            Appointment Types
          </button>
          <button
            onClick={() => setSettingsSection('blocks')}
            className={cn(
              'flex items-center gap-1.5 pb-2 text-sm font-medium border-b-2 transition-colors',
              settingsSection === 'blocks'
                ? 'border-primary text-foreground'
                : 'border-transparent text-muted-foreground hover:text-foreground'
            )}
          >
            <CalendarOff className="h-4 w-4" />
            Schedule Blocks
          </button>
          <button
            onClick={() => setSettingsSection('preferences')}
            className={cn(
              'flex items-center gap-1.5 pb-2 text-sm font-medium border-b-2 transition-colors',
              settingsSection === 'preferences'
                ? 'border-primary text-foreground'
                : 'border-transparent text-muted-foreground hover:text-foreground'
            )}
          >
            <Brain className="h-4 w-4" />
            Scheduling Notes
          </button>
        </div>
      </div>

      {settingsSection === 'blocks' ? (
        <ScheduleBlocksPanel />
      ) : settingsSection === 'preferences' ? (
        <PreferencesPanel />
      ) : (
        <div className="flex flex-1 min-h-0 overflow-hidden">
          {/* Left Panel */}
          <div className="w-72 border-r flex flex-col shrink-0">
            <Tabs
              value={activeTab}
              onValueChange={(tab) => {
                setActiveTab(tab);
                if (tab === 'types') {
                  setSelectedProviderId(null);
                  setSelectedLocationId(null);
                }
              }}
              className="flex flex-col h-full gap-0"
            >
              <div className="px-3 pt-3 shrink-0">
                <TabsList className="w-full">
                  <TabsTrigger value="types" className="flex-1">
                    Types
                  </TabsTrigger>
                  <TabsTrigger value="providers" className="flex-1">
                    Providers
                  </TabsTrigger>
                </TabsList>
              </div>

              {/* Types Tab */}
              <TabsContent value="types" className="flex-1 min-h-0 mt-0">
                <ScrollArea className="h-full">
                  <div className="p-2">
                    {!showCreateForm ? (
                      <div className="flex gap-1.5 mb-2">
                        <Button
                          variant="outline"
                          size="sm"
                          className="flex-1 text-xs"
                          onClick={() => setShowCreateForm(true)}
                        >
                          <Plus className="h-3 w-3 mr-1" />
                          Add Type
                        </Button>
                        <Button
                          variant="outline"
                          size="sm"
                          className="text-xs"
                          onClick={() => setSimulatorOpen(true)}
                          title="Test appointment type selection"
                        >
                          <FlaskConical className="h-3 w-3" />
                        </Button>
                      </div>
                    ) : (
                      <div className="mb-2 rounded-lg border p-2 space-y-2">
                        <Input
                          value={newTypeName}
                          onChange={(e) => setNewTypeName(e.target.value)}
                          placeholder="Name (required)"
                          className="h-7 text-xs"
                          autoFocus
                        />
                        <Input
                          value={newTypeShortName}
                          onChange={(e) => setNewTypeShortName(e.target.value.toUpperCase())}
                          placeholder="Short name, e.g. NP (required)"
                          className="h-7 text-xs font-mono"
                          maxLength={6}
                        />
                        <div className="flex gap-1.5">
                          <Button
                            variant="default"
                            size="sm"
                            className="flex-1 h-7 text-xs"
                            disabled={
                              !newTypeName.trim() ||
                              !newTypeShortName.trim() ||
                              createTypeMutation.isPending
                            }
                            onClick={() => {
                              const name = newTypeName.trim();
                              const shortName = newTypeShortName.trim();
                              // Client-side duplicate name check
                              if (types.some((t) => t.name.toLowerCase() === name.toLowerCase())) {
                                toast.error('An appointment type with this name already exists');
                                return;
                              }
                              const code = `type-${Date.now()}`;
                              createTypeMutation.mutate(
                                {
                                  code,
                                  name,
                                  shortName,
                                  durationMinutes: 30,
                                  doubleBooking: 1,
                                  color: COLOR_SWATCHES[types.length % COLOR_SWATCHES.length]!,
                                },
                                {
                                  onSuccess: (created) => {
                                    setSelectedId(created.id);
                                    setShowCreateForm(false);
                                    setNewTypeName('');
                                    setNewTypeShortName('');
                                  },
                                }
                              );
                            }}
                          >
                            {createTypeMutation.isPending ? (
                              <Loader2 className="h-3 w-3 mr-1 animate-spin" />
                            ) : (
                              <Plus className="h-3 w-3 mr-1" />
                            )}
                            Create
                          </Button>
                          <Button
                            variant="ghost"
                            size="sm"
                            className="h-7 text-xs"
                            onClick={() => {
                              setShowCreateForm(false);
                              setNewTypeName('');
                              setNewTypeShortName('');
                            }}
                          >
                            Cancel
                          </Button>
                        </div>
                      </div>
                    )}
                    {typesLoading && (
                      <div className="flex items-center justify-center py-8">
                        <Loader2 className="h-5 w-5 animate-spin text-muted-foreground" />
                      </div>
                    )}
                    {!typesLoading && types.length === 0 && (
                      <div className="px-3 py-6 text-center text-sm text-muted-foreground">
                        No appointment types yet. Add one above or sync from your EHR.
                      </div>
                    )}
                    {types.map((serverType) => {
                      // Merge local edits for the selected type so sidebar reflects changes instantly
                      const type =
                        serverType.id === selectedId
                          ? ({ ...serverType, ...editingFields } as AppointmentTypeResponse)
                          : serverType;
                      const typeColor = type.color ?? 'var(--chart-1)';
                      return (
                        <button
                          key={type.id}
                          onClick={() => guardDirtyAction(() => setSelectedId(type.id))}
                          className={cn(
                            'w-full flex items-center gap-3 rounded-lg px-3 py-2.5 text-left text-sm transition-colors',
                            'hover:bg-accent hover:text-accent-foreground',
                            selectedId === type.id && !selectedProvider
                              ? 'bg-accent text-accent-foreground'
                              : 'text-foreground'
                          )}
                        >
                          {type.shortName ? (
                            <Badge
                              variant="outline"
                              className="font-mono text-xs shrink-0 min-w-[3rem] justify-center"
                              style={{ borderColor: typeColor, color: typeColor }}
                            >
                              {type.shortName}
                            </Badge>
                          ) : (
                            <div
                              className="h-5 w-1.5 rounded-full shrink-0"
                              style={{ backgroundColor: typeColor }}
                            />
                          )}
                          <div className="flex-1 min-w-0">
                            <div className="font-medium truncate">{type.name}</div>
                            <div className="flex items-center gap-1 text-xs text-muted-foreground">
                              <Clock className="h-3 w-3" />
                              {type.durationMinutes} min
                            </div>
                          </div>
                        </button>
                      );
                    })}
                  </div>
                </ScrollArea>
              </TabsContent>

              {/* Providers Tab */}
              <TabsContent value="providers" className="flex-1 min-h-0 mt-0">
                <ScrollArea className="h-full">
                  <div className="p-2">
                    {providersLoading && (
                      <div className="flex items-center justify-center py-8">
                        <Loader2 className="h-5 w-5 animate-spin text-muted-foreground" />
                      </div>
                    )}
                    {providers.map((provider) => {
                      const isExpanded = expandedProviders.has(provider.id);
                      const isDefaultSelected =
                        selectedProviderId === provider.id && !selectedLocationId;

                      return (
                        <div key={provider.id} className="mb-1">
                          <button
                            onClick={() => {
                              toggleProviderExpanded(provider.id);
                              if (!isExpanded) {
                                setSelectedProviderId(provider.id);
                                setSelectedLocationId(null);
                                setExpandedTypeCards(new Set());
                              }
                            }}
                            className={cn(
                              'w-full flex items-center gap-2 rounded-lg px-2 py-2 text-left text-sm transition-colors',
                              'hover:bg-accent hover:text-accent-foreground'
                            )}
                          >
                            {isExpanded ? (
                              <ChevronDown className="h-4 w-4 shrink-0 text-muted-foreground" />
                            ) : (
                              <ChevronRight className="h-4 w-4 shrink-0 text-muted-foreground" />
                            )}
                            <User className="h-4 w-4 shrink-0 text-muted-foreground" />
                            <div className="min-w-0 flex-1">
                              <div className="font-medium truncate">
                                {provider.firstName} {provider.lastName}
                              </div>
                              <div className="text-xs text-muted-foreground">
                                {provider.specialty ?? ''}
                              </div>
                            </div>
                          </button>

                          {isExpanded && (
                            <div className="ml-4 pl-4 border-l">
                              <button
                                onClick={() => {
                                  setSelectedProviderId(provider.id);
                                  setSelectedLocationId(null);
                                  setExpandedTypeCards(new Set());
                                }}
                                className={cn(
                                  'w-full flex items-center gap-2 rounded-lg px-2 py-1.5 text-left text-sm transition-colors',
                                  'hover:bg-accent hover:text-accent-foreground',
                                  isDefaultSelected &&
                                    'bg-accent text-accent-foreground font-medium'
                                )}
                              >
                                <span className="text-muted-foreground">Default</span>
                              </button>

                              {expandedProviders.has(provider.id) &&
                                selectedProviderId === provider.id &&
                                providerLocations.map((pl) => {
                                  const isLocSelected =
                                    selectedProviderId === provider.id &&
                                    selectedLocationId === pl.locationId;
                                  return (
                                    <button
                                      key={pl.locationId}
                                      onClick={() => {
                                        setSelectedProviderId(provider.id);
                                        setSelectedLocationId(pl.locationId);
                                        setExpandedTypeCards(new Set());
                                      }}
                                      className={cn(
                                        'w-full flex items-center gap-2 rounded-lg px-2 py-1.5 text-left text-sm transition-colors',
                                        'hover:bg-accent hover:text-accent-foreground',
                                        isLocSelected &&
                                          'bg-accent text-accent-foreground font-medium'
                                      )}
                                    >
                                      <MapPin className="h-3.5 w-3.5 shrink-0 text-muted-foreground" />
                                      <span className="truncate flex-1">{pl.location.name}</span>
                                    </button>
                                  );
                                })}
                            </div>
                          )}
                        </div>
                      );
                    })}
                  </div>
                </ScrollArea>
              </TabsContent>
            </Tabs>
          </div>

          {/* ── Right Panel: Provider View ─────────────────────────────────────────── */}
          {selectedProvider && activeOverrides && (
            <div className="flex-1 overflow-y-auto min-w-0 min-h-0">
              <div className="p-4 border-b sticky top-0 bg-background z-10">
                <h3 className="font-semibold">
                  {selectedProvider.firstName} {selectedProvider.lastName}
                </h3>
                <p className="text-sm text-muted-foreground">
                  {selectedLocation
                    ? (selectedLocation.location?.name ?? 'Location')
                    : 'Default settings for all locations'}
                </p>
              </div>
              <div className="p-4 space-y-3">
                {types.map((typeDef) => {
                  const provOverride = overridesByTypeId.get(typeDef.id);
                  const isEnabled = provOverride?.enabled ?? true;
                  const overrideConstraints = parseOverrideConstraints(
                    provOverride?.constraints as Record<string, unknown>
                  );
                  const isExpanded = expandedTypeCards.has(typeDef.id);
                  const overrideCount =
                    (provOverride?.durationOverride != null ? 1 : 0) +
                    Object.keys(overrideConstraints).length;
                  const displayDuration = provOverride?.durationOverride ?? typeDef.durationMinutes;

                  return (
                    <div
                      key={typeDef.id}
                      className={cn(
                        'rounded-lg border transition-colors',
                        !isEnabled && 'opacity-50'
                      )}
                    >
                      {/* Card Header */}
                      <div
                        className="flex items-center gap-3 px-4 py-3 cursor-pointer"
                        onClick={() =>
                          isEnabled &&
                          setExpandedTypeCards((prev) => {
                            const next = new Set(prev);
                            if (next.has(typeDef.id)) next.delete(typeDef.id);
                            else next.add(typeDef.id);
                            return next;
                          })
                        }
                      >
                        {isEnabled &&
                          (isExpanded ? (
                            <ChevronDown className="h-4 w-4 shrink-0 text-muted-foreground" />
                          ) : (
                            <ChevronRight className="h-4 w-4 shrink-0 text-muted-foreground" />
                          ))}
                        <Badge
                          variant="outline"
                          className="font-mono text-xs shrink-0 min-w-[3rem] justify-center"
                          style={{
                            borderColor: typeDef.color ?? 'var(--chart-1)',
                            color: typeDef.color ?? 'var(--chart-1)',
                          }}
                        >
                          {typeDef.shortName ?? ''}
                        </Badge>
                        <div className="flex-1 min-w-0">
                          <div className="font-medium text-sm">{typeDef.name}</div>
                          <div className="flex items-center gap-2 text-xs text-muted-foreground">
                            <span className="flex items-center gap-1">
                              <Clock className="h-3 w-3" />
                              {displayDuration} min
                            </span>
                            <span>{typeDef.category ?? ''}</span>
                          </div>
                        </div>
                        {overrideCount > 0 && isEnabled && (
                          <Badge variant="secondary" className="text-xs">
                            {overrideCount} override
                            {overrideCount !== 1 ? 's' : ''}
                          </Badge>
                        )}
                        <Switch
                          checked={isEnabled}
                          onCheckedChange={() => toggleProviderTypeEnabled(typeDef.id)}
                          onClick={(e) => e.stopPropagation()}
                        />
                      </div>

                      {/* Expanded Detail */}
                      {isExpanded && isEnabled && (
                        <>
                          <Separator />
                          <div className="px-4 py-4 space-y-4">
                            <div className="text-sm text-muted-foreground">
                              {typeDef.description ?? ''}
                            </div>

                            <Separator />

                            {/* Duration — overridable */}
                            <OverridableField
                              label="Duration (min)"
                              defaultValue={String(resolveDefaultDuration(typeDef.id))}
                              overrideValue={
                                provOverride?.durationOverride != null
                                  ? String(provOverride.durationOverride)
                                  : null
                              }
                              onToggle={(on) => {
                                if (on) {
                                  setDurationOverride(
                                    typeDef.id,
                                    resolveDefaultDuration(typeDef.id)
                                  );
                                } else {
                                  clearDurationOverride(typeDef.id);
                                }
                              }}
                              onChange={(val) =>
                                setDurationOverride(typeDef.id, parseInt(val) || 0)
                              }
                            />

                            {/* Constraints */}
                            {(() => {
                              const typeConstraintsArr = (typeDef.constraints ??
                                []) as Constraint[];
                              const hasConstraints =
                                typeConstraintsArr.length > 0 ||
                                Object.keys(overrideConstraints).length > 0;
                              const usedTypes = new Set([
                                ...typeConstraintsArr.map((c) => c.type),
                                ...Object.keys(overrideConstraints),
                              ]);
                              const availableForProvider = CONSTRAINT_OPTIONS.filter((opt) => {
                                if (opt.scope === 'type') return false;
                                if (opt.type === 'businessHours' && !selectedLocationId)
                                  return false;
                                return !usedTypes.has(opt.type);
                              });

                              return (
                                <>
                                  <Separator />
                                  <div className="space-y-3">
                                    <div className="flex items-center justify-between">
                                      <h4 className="text-xs font-medium text-muted-foreground uppercase tracking-wider">
                                        Constraints
                                      </h4>
                                      {availableForProvider.length > 0 && (
                                        <DropdownMenu>
                                          <DropdownMenuTrigger asChild>
                                            <Button
                                              variant="outline"
                                              size="sm"
                                              className="h-7 text-xs"
                                            >
                                              <Plus className="h-3 w-3 mr-1" />
                                              Add
                                            </Button>
                                          </DropdownMenuTrigger>
                                          <DropdownMenuContent align="end" className="w-72">
                                            {availableForProvider.map((opt) => {
                                              const Icon = opt.icon;
                                              return (
                                                <DropdownMenuItem
                                                  key={opt.type}
                                                  onClick={() =>
                                                    addConstraintFromOption(typeDef.id, opt)
                                                  }
                                                  className="flex items-start gap-3 py-2.5 px-3 cursor-pointer"
                                                >
                                                  <Icon className="h-4 w-4 mt-0.5 shrink-0 text-muted-foreground" />
                                                  <div className="min-w-0">
                                                    <div className="text-sm font-medium">
                                                      {opt.label}
                                                    </div>
                                                    <div className="text-xs text-muted-foreground">
                                                      {opt.description}
                                                    </div>
                                                  </div>
                                                </DropdownMenuItem>
                                              );
                                            })}
                                          </DropdownMenuContent>
                                        </DropdownMenu>
                                      )}
                                    </div>

                                    {/* Type-level constraints (overridable) */}
                                    {typeConstraintsArr.map((constraint) => {
                                      const defaultConfig = resolveDefaultConstraint(
                                        typeDef.id,
                                        constraint.type
                                      );
                                      if (!defaultConfig) return null;
                                      const overrideEntry = overrideConstraints[constraint.type];

                                      return (
                                        <ConstraintOverrideCard
                                          key={constraint.id}
                                          constraint={constraint}
                                          defaultConfig={defaultConfig}
                                          overrideConfig={overrideEntry?.config}
                                          onToggle={(on) => {
                                            if (on) {
                                              setConstraintOverride(
                                                typeDef.id,
                                                constraint.type,
                                                constraint.label,
                                                structuredClone(defaultConfig)
                                              );
                                            } else {
                                              removeConstraintOverride(typeDef.id, constraint.type);
                                            }
                                          }}
                                          onChange={(config) =>
                                            setConstraintOverride(
                                              typeDef.id,
                                              constraint.type,
                                              constraint.label,
                                              config
                                            )
                                          }
                                        />
                                      );
                                    })}

                                    {/* Additional constraints (provider/location-level, not on base type) */}
                                    {Object.entries(overrideConstraints)
                                      .filter(
                                        ([cType]) =>
                                          !typeConstraintsArr.some((c) => c.type === cType)
                                      )
                                      .map(([cType, entry]) => {
                                        const optDef = CONSTRAINT_OPTIONS.find(
                                          (o) => o.type === cType
                                        );
                                        const Icon = optDef?.icon ?? ClipboardList;
                                        return (
                                          <div key={cType} className="flex items-start gap-3">
                                            <Icon className="h-4 w-4 mt-1 shrink-0 text-muted-foreground" />
                                            <div className="flex-1 min-w-0 space-y-2">
                                              <span className="text-sm font-medium">
                                                {entry.label}
                                              </span>
                                              <ConstraintEditor
                                                config={entry.config}
                                                onChange={(config) =>
                                                  setConstraintOverride(
                                                    typeDef.id,
                                                    cType,
                                                    entry.label,
                                                    config
                                                  )
                                                }
                                              />
                                            </div>
                                            <Button
                                              variant="ghost"
                                              size="icon"
                                              className="h-7 w-7 shrink-0 text-muted-foreground hover:text-destructive"
                                              onClick={() =>
                                                removeConstraintOverride(typeDef.id, cType)
                                              }
                                            >
                                              <X className="h-3.5 w-3.5" />
                                            </Button>
                                          </div>
                                        );
                                      })}

                                    {!hasConstraints && (
                                      <div className="text-sm text-muted-foreground">
                                        No constraints on this type.
                                      </div>
                                    )}
                                  </div>
                                </>
                              );
                            })()}
                          </div>
                        </>
                      )}
                    </div>
                  );
                })}
              </div>
            </div>
          )}

          {/* ── Right Panel: Type Detail Form ──────────────────────────────────────── */}
          {!selectedProvider && selectedType && (
            <div className="flex-1 flex flex-col min-w-0 min-h-0">
              <div className="flex-1 overflow-y-auto p-6 space-y-6">
                {/* Header */}
                <div className="flex items-center gap-3">
                  {selectedType.shortName ? (
                    <Badge
                      variant="outline"
                      className="font-mono text-sm px-3 py-1"
                      style={{
                        borderColor: selectedType.color ?? 'var(--chart-1)',
                        color: selectedType.color ?? 'var(--chart-1)',
                      }}
                    >
                      {selectedType.shortName}
                    </Badge>
                  ) : (
                    <div
                      className="h-8 w-2 rounded-full shrink-0"
                      style={{ backgroundColor: selectedType.color ?? 'var(--chart-1)' }}
                    />
                  )}
                  <div>
                    <h2 className="text-xl font-semibold">{selectedType.name}</h2>
                    <span className="text-sm text-muted-foreground">
                      {selectedType.category ?? ''}
                    </span>
                  </div>
                </div>

                <Separator />

                {/* Basic Fields */}
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-1">
                    <Label htmlFor="type-name">Name *</Label>
                    <Input
                      id="type-name"
                      value={selectedType.name}
                      onChange={(e) => updateSelectedType({ name: e.target.value })}
                      className={fieldErrors.name ? 'border-destructive' : ''}
                    />
                    <FieldError error={fieldErrors.name} />
                  </div>
                  <div className="space-y-1">
                    <Label htmlFor="type-shortname">Short Name *</Label>
                    <Input
                      id="type-shortname"
                      value={selectedType.shortName ?? ''}
                      onChange={(e) => updateSelectedType({ shortName: e.target.value })}
                      className={cn('font-mono', fieldErrors.shortName && 'border-destructive')}
                    />
                    <FieldError error={fieldErrors.shortName} />
                  </div>
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-1">
                    <Label htmlFor="type-category">Category</Label>
                    <Select
                      value={selectedType.category ?? ''}
                      onValueChange={(value) => updateSelectedType({ category: value })}
                    >
                      <SelectTrigger id="type-category" className="w-full">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        {CATEGORIES.map((cat) => (
                          <SelectItem key={cat} value={cat}>
                            {cat}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="space-y-1">
                    <Label htmlFor="type-duration">Duration (minutes) *</Label>
                    <Input
                      id="type-duration"
                      type="number"
                      className={cn(
                        NO_SPINNERS,
                        fieldErrors.durationMinutes && 'border-destructive'
                      )}
                      value={selectedType.durationMinutes}
                      onChange={(e) =>
                        updateSelectedType({
                          durationMinutes: parseInt(e.target.value) || 0,
                        })
                      }
                    />
                    <FieldError error={fieldErrors.durationMinutes} />
                  </div>
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-1">
                    <Label htmlFor="type-doublebooking">Double Booking (concurrent slots)</Label>
                    <Input
                      id="type-doublebooking"
                      type="number"
                      className={NO_SPINNERS}
                      value={selectedType.doubleBooking}
                      onChange={(e) =>
                        updateSelectedType({
                          doubleBooking: parseInt(e.target.value) || 0,
                        })
                      }
                      min={0}
                    />
                  </div>
                  <div className="space-y-1">
                    <Label>Color *</Label>
                    <ColorPicker
                      value={selectedType.color ?? 'var(--chart-1)'}
                      onChange={(color) => updateSelectedType({ color })}
                    />
                    <FieldError error={fieldErrors.color} />
                  </div>
                </div>

                <div className="space-y-2">
                  <Label htmlFor="type-description">Description</Label>
                  <Textarea
                    id="type-description"
                    value={selectedType.description ?? ''}
                    onChange={(e) => updateSelectedType({ description: e.target.value })}
                    rows={3}
                  />
                </div>

                <Separator />

                {/* Diagnosis & Service Codes */}
                <div className="space-y-2">
                  <Label>Diagnosis Codes (ICD-10)</Label>
                  <CodeAutocomplete
                    codeType="icd10cm"
                    tags={selectedType.icdCodes ?? []}
                    onChange={(icdCodes) => updateSelectedType({ icdCodes })}
                    placeholder="Search by code or description..."
                  />
                </div>

                <div className="space-y-2">
                  <Label>Service / Procedure Codes (CPT)</Label>
                  <CodeAutocomplete
                    codeType="cpt"
                    tags={selectedType.serviceCodes ?? []}
                    onChange={(serviceCodes) => updateSelectedType({ serviceCodes })}
                    placeholder="Search by code or description..."
                  />
                </div>

                <Separator />

                {/* Constraints */}
                <div className="space-y-3">
                  <div className="flex items-center justify-between">
                    <div>
                      <h3 className="text-sm font-medium">Constraints</h3>
                      <p className="text-xs text-muted-foreground">
                        Optional rules that limit when this type can be booked
                      </p>
                    </div>
                    <DropdownMenu>
                      <DropdownMenuTrigger asChild>
                        <Button
                          variant="outline"
                          size="sm"
                          disabled={availableConstraints.length === 0}
                        >
                          <Plus className="h-4 w-4 mr-1" />
                          Add Constraint
                        </Button>
                      </DropdownMenuTrigger>
                      <DropdownMenuContent align="end" className="w-72">
                        {availableConstraints.map((opt) => {
                          const Icon = opt.icon;
                          return (
                            <DropdownMenuItem
                              key={opt.type}
                              onClick={() => addConstraint(opt)}
                              className="flex items-start gap-3 py-2.5 px-3 cursor-pointer"
                            >
                              <Icon className="h-4 w-4 mt-0.5 shrink-0 text-muted-foreground" />
                              <div className="min-w-0">
                                <div className="text-sm font-medium">{opt.label}</div>
                                <div className="text-xs text-muted-foreground">
                                  {opt.description}
                                </div>
                              </div>
                            </DropdownMenuItem>
                          );
                        })}
                      </DropdownMenuContent>
                    </DropdownMenu>
                  </div>

                  {typeConstraints.length === 0 ? (
                    <div className="rounded-lg border border-dashed p-4 text-center text-sm text-muted-foreground">
                      No constraints. This appointment type can be booked without restrictions.
                    </div>
                  ) : (
                    <div className="space-y-2">
                      {typeConstraints.map((constraint) => {
                        const optDef = CONSTRAINT_OPTIONS.find((o) => o.type === constraint.type);
                        const Icon = optDef?.icon ?? ClipboardList;
                        return (
                          <div
                            key={constraint.id}
                            data-constraint-id={constraint.id}
                            className="rounded-lg border px-3 py-3 space-y-2"
                          >
                            <div className="flex items-center gap-3">
                              <Icon className="h-4 w-4 shrink-0 text-muted-foreground" />
                              <div className="flex-1 min-w-0">
                                <div className="text-sm font-medium">{constraint.label}</div>
                                {optDef?.description && (
                                  <p className="text-xs text-muted-foreground">
                                    {optDef.description}
                                  </p>
                                )}
                              </div>
                              <Button
                                variant="ghost"
                                size="icon"
                                className="h-8 w-8 shrink-0 text-muted-foreground hover:text-destructive"
                                onClick={() => removeConstraint(constraint.id)}
                              >
                                <X className="h-4 w-4" />
                              </Button>
                            </div>
                            <div className="pl-7">
                              <ConstraintEditor
                                config={constraint.config}
                                onChange={(config) => updateConstraintConfig(constraint.id, config)}
                              />
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  )}
                </div>

                {/* Provider Overrides summary — only show if overrides exist */}
                {typeOverridesForSummary && typeOverridesForSummary.length > 0 && (
                  <>
                    <Separator />
                    <Collapsible open={overridesOpen} onOpenChange={setOverridesOpen}>
                      <CollapsibleTrigger asChild>
                        <button className="flex items-center justify-between w-full">
                          <div>
                            <h3 className="text-sm font-medium">Provider Overrides</h3>
                            <p className="text-xs text-muted-foreground">
                              Providers with custom settings for this type
                            </p>
                          </div>
                          <ChevronsUpDown className="h-4 w-4 text-muted-foreground" />
                        </button>
                      </CollapsibleTrigger>
                      <CollapsibleContent>
                        <div className="mt-3 space-y-2">
                          {(() => {
                            if (!typeOverridesForSummary || typeOverridesForSummary.length === 0) {
                              return (
                                <div className="text-sm text-muted-foreground text-center py-2">
                                  No provider overrides for this type.
                                </div>
                              );
                            }
                            // Group overrides by provider
                            const byProvider = new Map<string, AppointmentTypeOverrideResponse[]>();
                            for (const ovr of typeOverridesForSummary) {
                              const list = byProvider.get(ovr.providerId) ?? [];
                              list.push(ovr);
                              byProvider.set(ovr.providerId, list);
                            }
                            return [...byProvider.entries()].map(([providerId, overrides]) => {
                              const provider = providers.find((p) => p.id === providerId);
                              if (!provider) return null;
                              const provLevelOvr = overrides.find((o) => !o.locationId);
                              const constraintOvr = parseOverrideConstraints(
                                provLevelOvr?.constraints as Record<string, unknown>
                              );
                              const overrideKeys = Object.keys(constraintOvr);
                              const hasDurationOverride = provLevelOvr?.durationOverride != null;
                              const totalOverrides =
                                overrideKeys.length + (hasDurationOverride ? 1 : 0);
                              const isDisabled = provLevelOvr && !provLevelOvr.enabled;

                              return (
                                <div
                                  key={providerId}
                                  className={cn(
                                    'rounded-lg border px-4 py-3',
                                    isDisabled && 'opacity-50'
                                  )}
                                >
                                  <div className="flex items-center gap-2">
                                    <User className="h-4 w-4 text-muted-foreground" />
                                    <div className="flex-1 min-w-0">
                                      <div className="text-sm font-medium">
                                        {provider.firstName} {provider.lastName}
                                      </div>
                                      <div className="text-xs text-muted-foreground">
                                        {provider.specialty ?? ''}
                                      </div>
                                    </div>
                                    {isDisabled && (
                                      <span className="text-xs text-muted-foreground">
                                        disabled
                                      </span>
                                    )}
                                    {!isDisabled && totalOverrides > 0 && (
                                      <Badge variant="secondary" className="text-xs">
                                        {totalOverrides} override
                                        {totalOverrides !== 1 ? 's' : ''}
                                      </Badge>
                                    )}
                                    {!isDisabled && totalOverrides === 0 && (
                                      <span className="text-xs text-muted-foreground">default</span>
                                    )}
                                  </div>
                                  {!isDisabled && totalOverrides > 0 && (
                                    <div className="mt-2 pt-2 border-t space-y-1">
                                      {hasDurationOverride && (
                                        <div className="flex items-center justify-between text-sm">
                                          <span className="text-muted-foreground">Duration</span>
                                          <span className="font-medium">
                                            {provLevelOvr!.durationOverride} min
                                          </span>
                                        </div>
                                      )}
                                      {overrideKeys.map((cType) => {
                                        const entry = constraintOvr[cType]!;
                                        return (
                                          <div
                                            key={cType}
                                            className="flex items-center justify-between text-sm"
                                          >
                                            <span className="text-muted-foreground">
                                              {entry.label}
                                            </span>
                                            <span className="font-medium">
                                              {constraintSummary(entry.config)}
                                            </span>
                                          </div>
                                        );
                                      })}
                                    </div>
                                  )}
                                </div>
                              );
                            });
                          })()}
                        </div>
                      </CollapsibleContent>
                    </Collapsible>
                  </>
                )}
              </div>

              {/* Sticky Footer — Save / Cancel / Delete */}
              <div className="shrink-0 border-t bg-background px-6 py-3 flex items-center justify-between">
                <Button
                  variant="ghost"
                  size="sm"
                  className="text-xs text-muted-foreground hover:text-destructive"
                  disabled={deleteTypeMutation.isPending}
                  onClick={() => {
                    if (!selectedType) return;
                    if (!confirm(`Delete "${selectedType.name}"? This cannot be undone.`)) return;
                    deleteTypeMutation.mutate(selectedType.id, {
                      onSuccess: () => {
                        setSelectedId(null);
                        toast.success('Appointment type deleted');
                      },
                    });
                  }}
                >
                  {deleteTypeMutation.isPending ? (
                    <Loader2 className="h-3 w-3 mr-1 animate-spin" />
                  ) : (
                    <Trash2 className="h-3 w-3 mr-1" />
                  )}
                  Delete
                </Button>
                <div className="flex items-center gap-2">
                  {isDirty && (
                    <Button variant="ghost" size="sm" className="text-xs" onClick={cancelEdits}>
                      Cancel
                    </Button>
                  )}
                  <Button
                    variant="default"
                    size="sm"
                    className="text-xs"
                    disabled={!isDirty || updateTypeMutation.isPending}
                    onClick={saveType}
                  >
                    {updateTypeMutation.isPending ? (
                      <Loader2 className="h-3 w-3 mr-1 animate-spin" />
                    ) : null}
                    Save
                  </Button>
                </div>
              </div>
            </div>
          )}
        </div>
      )}

      <UnsavedChangesDialog
        open={showUnsavedDialog}
        onCancel={() => setShowUnsavedDialog(false)}
        onDiscard={() => {
          setShowUnsavedDialog(false);
          cancelEdits();
          pendingActionRef.current?.();
        }}
        onSave={() => {
          setShowUnsavedDialog(false);
          if (saveType()) {
            pendingActionRef.current?.();
          }
          // If saveType() returns false (validation errors), stay — user sees errors
        }}
      />
      <AppointmentTypeSimulatorDialog open={simulatorOpen} onOpenChange={setSimulatorOpen} />
    </div>
  );
}
